/**
 * Real `getStats()` — the input to the degradation ladder (`degradation.ts`).
 *
 * Sampled from every subscribed receiver, not from a heuristic: `downlinkBps`
 * is the measured byte delta and `packetLoss` the measured loss ratio over the
 * same window. The ladder's 5 % threshold is only meaningful because of this.
 */
import type { Room } from 'livekit-client';
import type { ConnectionQuality } from './contract';

export interface StatsSample {
  readonly at: number;
  readonly bytes: number;
  readonly packets: number;
  readonly lost: number;
}

export interface StatsReading {
  readonly downlinkBps: number;
  readonly packetLoss: number;
}

export const ZERO_SAMPLE: StatsSample = { at: 0, bytes: 0, packets: 0, lost: 0 };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const num = (source: Record<string, unknown>, key: string): number => {
  const value = source[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
};

/** Sums `inbound-rtp` across every subscribed remote track in the room. */
export async function sampleInbound(room: Room): Promise<StatsSample> {
  const reports = await Promise.all(
    [...room.remoteParticipants.values()].flatMap((participant) =>
      [...participant.trackPublications.values()]
        .filter((publication) => publication.isSubscribed)
        .map((publication) => publication.track?.getRTCStatsReport() ?? Promise.resolve(undefined)),
    ),
  );

  let bytes = 0;
  let packets = 0;
  let lost = 0;
  for (const report of reports) {
    report?.forEach((entry: unknown) => {
      if (!isRecord(entry) || entry['type'] !== 'inbound-rtp') return;
      bytes += num(entry, 'bytesReceived');
      packets += num(entry, 'packetsReceived');
      lost += num(entry, 'packetsLost');
    });
  }
  return { at: Date.now(), bytes, packets, lost };
}

/** Delta between two samples. Counters only ever grow, so negatives are drops. */
export function readingFrom(previous: StatsSample, next: StatsSample): StatsReading {
  const elapsedMs = next.at - previous.at;
  if (previous.at === 0 || elapsedMs <= 0) return { downlinkBps: 0, packetLoss: 0 };

  const bytes = Math.max(0, next.bytes - previous.bytes);
  const received = Math.max(0, next.packets - previous.packets);
  const lost = Math.max(0, next.lost - previous.lost);
  const total = received + lost;

  return {
    downlinkBps: Math.round((bytes * 8 * 1000) / elapsedMs),
    packetLoss: total === 0 ? 0 : Number((lost / total).toFixed(4)),
  };
}

/** Loss alone can call the room lost before LiveKit's own quality signal does. */
export function worseQuality(a: ConnectionQuality, b: ConnectionQuality): ConnectionQuality {
  const order: readonly ConnectionQuality[] = ['excellent', 'good', 'poor', 'lost'];
  return order.indexOf(a) >= order.indexOf(b) ? a : b;
}
